import * as p from '@clack/prompts'
import pc from 'picocolors'

import { ensureVercelAuth } from './auth.js'
import { provisionNeon, provisionNeonInteractive } from './neon.js'
import { createVercelProject } from './project.js'
import { provisionResendInteractive } from './resend.js'
import { resolveVercelRunner, VERCEL_CLI_VERSION } from './runner.js'
import type {
  AuthFailureReason,
  NeonFailureReason,
  NeonProvisionResult,
  ResendFailureReason,
  VercelNeonFlowResult,
  VercelRunner
} from './types.js'

/**
 * Top-level orchestrator for the Vercel-backed services flow during
 * `betterstart init`: resolve a runner → ensure auth (interactive login link) →
 * create + link a Vercel project → provision Neon and capture the DATABASE_URL
 * → optionally provision Resend via the Vercel marketplace and capture the
 * RESEND_API_KEY.
 *
 * When interactive, provisioning runs attached to the terminal so the user can
 * accept marketplace terms and pick the Free plan in Vercel's own prompts — the
 * non-interactive (`--format json`) path is reserved for `--yes` + token.
 *
 * Never throws. Any failure before a DATABASE_URL is captured resolves to
 * `{ ok: false }` so the caller can fall back to manual connection-string
 * entry. A Resend failure keeps `ok: true` (the database is provisioned) and
 * simply omits `resendApiKey`, so the caller falls back to the email prompt.
 */
export async function runVercelNeonFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  plan?: string
  env?: NodeJS.ProcessEnv
  /** Also provision Resend after Neon succeeds. Interactive mode only. */
  provisionResend?: boolean
}): Promise<VercelNeonFlowResult> {
  const env = options.env ?? process.env

  try {
    const runnerSpinner = p.spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.stop(
      runner.source === 'path'
        ? 'Using the Vercel CLI from your PATH'
        : `Using ${pc.cyan(`npx vercel@${VERCEL_CLI_VERSION}`)}`
    )

    // Auth owns its own spinners and stops them before any interactive login.
    const auth = await ensureVercelAuth(runner, options.cwd, { env })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }

    // Create + link the Vercel project (non-interactive; safe under a spinner).
    const projectSpinner = p.spinner()
    projectSpinner.start(`Creating Vercel project ${pc.cyan(options.projectName)}`)
    const project = await createVercelProject(runner, options.cwd, options.projectName, env)
    projectSpinner.stop(
      project.linked
        ? `Linked Vercel project ${pc.cyan(project.name)}`
        : `Using Vercel project ${pc.cyan(project.name)}`
    )

    const neon = await provisionNeonForMode(runner, options)
    if (neon.failure || !neon.databaseUrl) {
      p.log.warn(neonFailureMessage(neon.failure))
      if (neon.detail) p.log.message(pc.dim(neon.detail))
      return { ok: false }
    }

    p.log.success(`Provisioned Neon Postgres for ${pc.cyan(project.name)}`)

    let resendApiKey: string | undefined
    if (options.provisionResend && options.interactive) {
      p.log.info(
        `Set up Resend email in the Vercel prompts below ${pc.dim('(the Free plan is recommended).')}`
      )
      const resend = await provisionResendInteractive(runner, options.cwd, { env })
      if (resend.apiKey) {
        resendApiKey = resend.apiKey
        p.log.success(`Provisioned Resend email for ${pc.cyan(project.name)}`)
      } else {
        p.log.warn(resendFailureMessage(resend.failure))
      }
    }

    return {
      ok: true,
      databaseUrl: neon.databaseUrl,
      vercelProjectId: project.projectId,
      neonResourceName: neon.resourceName,
      resendApiKey
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  }
}

function provisionNeonForMode(
  runner: VercelRunner,
  options: { cwd: string; interactive: boolean; plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const opts = { plan: options.plan, env: options.env }
  if (options.interactive) {
    p.log.info(
      `Create your Neon database in the Vercel prompts below ${pc.dim('(the Free plan is recommended).')}`
    )
    return provisionNeonInteractive(runner, options.cwd, opts)
  }
  return provisionNeon(runner, options.cwd, opts)
}

function authFailureMessage(reason?: AuthFailureReason): string {
  switch (reason) {
    case 'login-cancelled':
      return 'Vercel sign-in was cancelled.'
    case 'login-failed':
      return 'Could not verify your Vercel sign-in.'
    case 'timeout':
      return 'Vercel sign-in timed out.'
    case 'no-runner':
      return 'The Vercel CLI is not available.'
    default:
      return 'Could not sign in to Vercel.'
  }
}

function resendFailureMessage(reason?: ResendFailureReason): string {
  switch (reason) {
    case 'env-pull-empty':
      return 'Provisioned Resend, but no RESEND_API_KEY came back — you can pick an email provider next.'
    case 'timeout':
      return 'Resend provisioning timed out — you can pick an email provider next.'
    default:
      return 'Resend provisioning did not complete — you can pick an email provider next.'
  }
}

function neonFailureMessage(reason?: NeonFailureReason): string {
  switch (reason) {
    case 'terms-required':
      return 'Provisioning needs the Neon marketplace terms accepted — falling back to manual entry.'
    case 'claim-required':
      return 'The Neon resource needs to be claimed — falling back to manual entry.'
    case 'env-pull-empty':
      return 'Provisioned Neon, but no DATABASE_URL came back — falling back to manual entry.'
    case 'timeout':
      return 'Neon provisioning timed out — falling back to manual entry.'
    default:
      return 'Neon provisioning did not complete — falling back to manual entry.'
  }
}
